import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Paper,
  MenuItem,
  Alert,
  Avatar,
  Stack,
  alpha,
  useTheme,
} from '@mui/material'
import {
  AutoAwesome as AutoAwesomeIcon,
  Bolt as BoltIcon,
  CheckCircle,
  CloudUpload,
  Inventory2 as PackageIcon,
  LocalOffer as PriceIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { useExpressAiCreateMutation } from '../../store/services/productApi'
import { useGetCategoriesQuery } from '../../store/services/categoryApi'
import ImageUpload from '../ImageUpload'

interface AgenticProductWizardProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

export const AgenticProductWizard: React.FC<AgenticProductWizardProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()
  const [expressAiCreate, { isLoading }] = useExpressAiCreateMutation()
  const { data: categoriesData } = useGetCategoriesQuery({ per_page: 100 })

  const [formData, setFormData] = useState({
    name: '',
    sale_price: '',
    inner_pack_size: '24',
    carton_size: '144',
    image: '',
    category_id: '',
    brand_name: '',
    key_features: '',
  })

  const [aiResult, setAiResult] = useState<any>(null)

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleExpressCreate = async () => {
    if (!formData.name.trim()) {
      enqueueSnackbar('Product Name is required', { variant: 'warning' })
      return
    }
    if (!formData.sale_price || Number(formData.sale_price) <= 0) {
      enqueueSnackbar('Valid Unit Sale Price is required', { variant: 'warning' })
      return
    }

    try {
      const selectedCategory = categoriesData?.items.find(c => c.id === formData.category_id)?.name
      const res = await expressAiCreate({
        name: formData.name,
        sale_price: Number(formData.sale_price),
        inner_pack_size: Number(formData.inner_pack_size) || 1,
        carton_size: Number(formData.carton_size) || 1,
        image: formData.image || undefined,
        category_id: formData.category_id || undefined,
        category_name: selectedCategory || undefined,
        brand_name: formData.brand_name || undefined,
        key_features: formData.key_features || undefined,
      }).unwrap()

      if (res?.product) {
        setAiResult(res.ai_insights)
        enqueueSnackbar(`Express Product Created! HSN Suggested: ${res.ai_insights.hsn_code}`, { variant: 'success' })
        if (onSuccess) onSuccess()
      }
    } catch (err: any) {
      enqueueSnackbar(err?.data?.detail || 'Failed to create product via Sarvam AI', { variant: 'error' })
    }
  }

  const handleReset = () => {
    setFormData({
      name: '',
      sale_price: '',
      inner_pack_size: '24',
      carton_size: '144',
      image: '',
      category_id: '',
      brand_name: '',
      key_features: '',
    })
    setAiResult(null)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, background: 'linear-gradient(135deg, #7c4dff 0%, #651fff 100%)', color: 'white', py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'white', color: '#651fff', width: 36, height: 36 }}>
              <AutoAwesomeIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.15rem' }}>
                1-Click Express AI Product Onboarding
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                Powered by Sarvam AI (`sarvam-105b`) — Auto HSN Code, GST & B2B Copy
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: '24px !important' }}>
        {aiResult ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircle sx={{ fontSize: 54, color: 'success.main', mb: 1 }} />
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Product Successfully Added to Catalog!
            </Typography>

            <Paper variant="outlined" sx={{ p: 2.5, mt: 2, textAlign: 'left', bgcolor: '#F8FAFC', borderRadius: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Suggested HSN:</Typography>
                  <Typography variant="body1" fontWeight={700} color="primary.main">{aiResult.hsn_code}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>GST Slab:</Typography>
                  <Typography variant="body1" fontWeight={700}>{aiResult.gst_rate}%</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Recommended MOQ:</Typography>
                  <Typography variant="body1" fontWeight={700}>{aiResult.recommended_moq} pcs</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Carton Rate:</Typography>
                  <Typography variant="body1" fontWeight={700} color="success.main">₹{aiResult.carton_rate}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>HSN Classification:</Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{aiResult.hsn_description}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloudUpload fontSize="small" color="primary" /> Product Cover Image
              </Typography>
              <ImageUpload
                value={formData.image}
                onChange={(url) => handleInputChange('image', url)}
              />
            </Grid>

            <Grid item xs={12} md={7}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PackageIcon fontSize="small" color="primary" /> Core B2B Inputs
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Product Name"
                    placeholder="e.g. Touch Cool Markers 24 Pcs Box"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Unit Sale Price (₹)"
                    type="number"
                    value={formData.sale_price}
                    onChange={(e) => handleInputChange('sale_price', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Category (Optional)"
                    value={formData.category_id}
                    onChange={(e) => handleInputChange('category_id', e.target.value)}
                  >
                    <MenuItem value="">Auto-Detect Category</MenuItem>
                    {categoriesData?.items.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Inner Box Qty (Pcs)"
                    type="number"
                    value={formData.inner_pack_size}
                    onChange={(e) => handleInputChange('inner_pack_size', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Master Carton Qty (Pcs)"
                    type="number"
                    value={formData.carton_size}
                    onChange={(e) => handleInputChange('carton_size', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Brand Name (Optional)"
                    placeholder="e.g. Touch Cool, Dhanshika"
                    value={formData.brand_name}
                    onChange={(e) => handleInputChange('brand_name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Key Highlights / Specs (Optional Prompt)"
                    placeholder="e.g. Dual tip markers, alcohol based ink, 24 vibrant colors..."
                    value={formData.key_features}
                    onChange={(e) => handleInputChange('key_features', e.target.value)}
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
        {aiResult ? (
          <>
            <Button onClick={handleReset} variant="outlined" color="primary">Create Another Product</Button>
            <Button onClick={onClose} variant="contained" color="success">Done</Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} color="inherit">Cancel</Button>
            <Button
              onClick={handleExpressCreate}
              disabled={isLoading}
              variant="contained"
              startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <BoltIcon />}
              sx={{
                background: 'linear-gradient(135deg, #7c4dff 0%, #651fff 100%)',
                color: 'white',
                fontWeight: 700,
                px: 3,
                '&:hover': { background: 'linear-gradient(135deg, #651fff 0%, #4527a0 100%)' }
              }}
            >
              {isLoading ? 'Sarvam AI Creating Product...' : '⚡ Express AI Create Product'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default AgenticProductWizard
